import React, { useEffect, useState } from "react";
import {
  Container,
  Card,
  Row,
  Col,
  Button,
  Badge,
  Spinner,
  Modal,
  ListGroup,
} from "react-bootstrap";
import axios from "axios";
import Swal from "sweetalert2";
import API_BASE_URL from "../config";
import GoogleCalendarView from "../components/GoogleCalendarView";
axios.defaults.withCredentials = true;
axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem("token")}`;

axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
});

const providers = [
  { key: "google", label: "Google Meet", icon: "bi-google", color: "danger" },
];

export default function MeetingIntegrationPage() {
  const [integrations, setIntegrations] = useState({});
  const [loading, setLoading] = useState(true);

  // Calendar Modal State
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);

  const getCompanyId = () => {
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        if (user && (user.company_id || user.companyId)) {
          return user.company_id || user.companyId;
        }
      }
    } catch (e) {
      console.error("Failed to parse user from localStorage", e);
    }
    return localStorage.getItem("selectedCompanyId");
  };

  const companyId = getCompanyId();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasSuccess = params.get("success");

    if (hasSuccess) {
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Integration connected!",
        timer: 2200,
        showConfirmButton: false,
      });
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    if (companyId) {
      fetchIntegrations();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchIntegrations = async () => {
    if (!companyId) return;

    try {
      setLoading(true);

      const userStr = localStorage.getItem("user");
      const user = userStr ? JSON.parse(userStr) : {};
      const userId = user.id || user.sub;

      const params = new URLSearchParams({
        company_id: companyId,
        ...(userId && { user_id: userId }),
      });

      const res = await axios.get(
        `${API_BASE_URL}/integrations?${params.toString()}`,
        { withCredentials: true }
      );

      const map = {};
      res.data.forEach((i) => {
        map[i.provider] = i;
      });

      setIntegrations(map);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 401) {
        Swal.fire("Session Expired", "Please login again.", "warning");
      } else {
        Swal.fire("Error", "Failed to load integrations", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  // ==================== Fetch Google Calendar ====================
  const fetchGoogleCalendar = async () => {
    if (!companyId) return;

    setCalendarLoading(true);
    try {
      const userStr = localStorage.getItem("user");
      const user = userStr ? JSON.parse(userStr) : {};
      const userId = user.id || user.sub;

      const params = new URLSearchParams({
        company_id: companyId,
        ...(userId && { user_id: userId }),
      });

      const res = await axios.get(
        `${API_BASE_URL}/integrations/google/calendar?${params.toString()}`
      );

      setCalendarEvents(res.data || []);
      setShowCalendarModal(true);
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to load Google Calendar", "error");
    } finally {
      setCalendarLoading(false);
    }
  };

  const handleDisconnect = async (provider) => {
    if (!companyId) {
      Swal.fire("Error", "No company selected.", "error");
      return;
    }

    const confirm = await Swal.fire({
      title: "Disconnect?",
      text: `Disconnect ${provider}?`,
      icon: "warning",
      showCancelButton: true,
    });

    if (!confirm.isConfirmed) return;

    try {
      await axios.delete(
        `${API_BASE_URL}/integrations/${provider}?company_id=${companyId}`,
        { withCredentials: true }
      );

      Swal.fire("Success", "Disconnected successfully", "success");
      fetchIntegrations();
    } catch (err) {
      Swal.fire("Error", "Failed to disconnect", "error");
    }
  };

  const handleConnect = (provider) => {
    if (!companyId) {
      Swal.fire("Error", "No company selected. Please select a company first.", "error");
      return;
    }
    const token = localStorage.getItem("token");
    if (!token) {
      Swal.fire("Error", "Authentication token not found. Please login again.", "error");
      return;
    }

    const url = `${API_BASE_URL}/integrations/${provider}/auth/${companyId}?token=${encodeURIComponent(token)}`;
    window.location.href = url;
  };

  const renderCard = (p) => {
    const integration = integrations[p.key];

    return (
      <Col xs={12} md={6} lg={4} key={p.key} className="mb-4">
        <Card className="shadow-sm border-0 h-100">
          <Card.Body className="d-flex flex-column justify-content-between">
            <div>
              <h5 className="d-flex align-items-center gap-2">
                <i className={`bi ${p.icon} text-${p.color}`}></i>
                {p.label}
              </h5>

              {integration ? (
                <>
                  <Badge bg="success" className="mb-2">Connected</Badge>
                  <div className="small text-muted">
                    {integration.email || "Account Connected"}
                  </div>
                </>
              ) : (
                <Badge bg="secondary">Not Connected</Badge>
              )}
            </div>

            <div className="mt-3 d-flex gap-2">
              {integration ? (
                <>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={fetchGoogleCalendar}
                  >
                    View Calendar
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleDisconnect(p.key)}
                  >
                    Disconnect
                  </Button>
                </>
              ) : (
                <Button
                  variant={`outline-${p.color}`}
                  size="sm"
                  onClick={() => handleConnect(p.key)}
                >
                  Connect
                </Button>
              )}
            </div>
          </Card.Body>
        </Card>
      </Col>
    );
  };

  return (
    <Container className="py-4">
      <h3 className="mb-4">Meeting Integrations</h3>

      {loading ? (
        <div className="text-center">
          <Spinner />
        </div>
      ) : (
        <Row>{providers.map(renderCard)}</Row>
      )}

      {/* Google Calendar Modal */}
      <Modal show={showCalendarModal} onHide={() => setShowCalendarModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Google Calendar</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {calendarLoading ? (
            <div className="text-center py-4">
              <Spinner />
            </div>
          ) : calendarEvents.length === 0 ? (
            <div className="text-center py-4 text-muted">
              No upcoming events found in your Google Calendar.
            </div>
          ) : (
           <GoogleCalendarView 
      companyId={companyId} 
      onClose={() => setShowCalendarModal(false)} 
    />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCalendarModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
